import type React from "react";
import { Input, NumberField as RACNumberField } from "react-aria-components";
import { tv } from "tailwind-variants";
import { cn } from "~/utils/cn";
import { Field, FieldError, fieldStyles } from "./Field";

export const inputStyles = tv({
  variants: {
    size: {
      large: "rounded-[0.625rem] py-[0.75rem] px-[1rem] body-md-400",
      medium: "rounded-[0.5rem] py-[0.5rem] px-[0.875rem] body-md-400",
      small: "rounded-[0.375rem] py-[0.25rem] px-[0.75rem] body-sm-400",
    },
    variant: {
      filter: "outline-none border-none !p-2 text-dark-600",
      quoteForm: "outline-none border-none !p-0 !rounded-none text-dark-1000",
      default:
        "border-outline-200 border flex w-full flex-1 items-center self-stretch bg-white text-dark-900 outline-none placeholder:text-dark-250 hover:border-dark-300 focus:border-dark-300 active:border-dark-300 disabled:text-dark-200",
      table:
        "w-full border-0 bg-transparent outline-none whitespace-nowrap hover:bg-dark-50 focus:bg-dark-50 focus:ring-0",
    },
    isError: fieldStyles.variants.isError,
    isDisabled: fieldStyles.variants.isDisabled,
  },
  defaultVariants: {
    size: "large",
    variant: "default",
  },
});

interface InputFieldProps extends Omit<React.ComponentProps<"input">, "size"> {
  label?: React.ReactNode;
  errorMessage?: string;
  size?: "large" | "medium" | "small";
  variant?: "default" | "table" | "filter" | "quoteForm";
  isDisabled?: boolean;
  containerClassName?: string;
}

interface TextAreaFieldProps
  extends Omit<React.ComponentProps<"textarea">, "size"> {
  label?: React.ReactNode;
  errorMessage?: string;
  size?: "large" | "medium" | "small";
  isDisabled?: boolean;
}

export function InputField({
  label,
  errorMessage,
  isDisabled,
  size,
  variant,
  className,
  containerClassName,
  ...props
}: InputFieldProps) {
  const labelWithRequiredIndicator =
    props.required && label ? `${label}*` : label;
  return (
    <Field label={labelWithRequiredIndicator} className={containerClassName}>
      <input
        className={inputStyles({
          size,
          variant,
          isError: Boolean(errorMessage),
          isDisabled,
          className,
        })}
        disabled={isDisabled}
        {...props}
      />
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </Field>
  );
}
interface TextFieldProps
  extends Omit<React.ComponentProps<"input">, "size" | "onChange"> {
  label?: React.ReactNode;
  errorMessage?: string;
  size?: "large" | "medium" | "small";
  variant?: "default" | "table" | "filter" | "quoteForm";
  isDisabled?: boolean;
  onChange?: (value: string) => void;
}

export function TextField({
  label,
  errorMessage,
  isDisabled,
  size,
  variant,
  className,
  onChange,
  ...props
}: TextFieldProps) {
  return (
    <Field label={label}>
      <input
        {...props}
        className={inputStyles({
          size,
          variant,
          isError: Boolean(errorMessage),
          isDisabled,
          className,
        })}
        disabled={isDisabled}
        onChange={(e) => {
          onChange?.(e.target.value);
        }}
      />
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </Field>
  );
}

export function TextAreaField({
  label,
  errorMessage,
  isDisabled,
  size,
  className,
  ...props
}: TextAreaFieldProps) {
  return (
    <Field label={label}>
      <textarea
        className={inputStyles({
          size,
          isError: Boolean(errorMessage),
          isDisabled,
          className,
        })}
        disabled={isDisabled}
        {...props}
      />
      <FieldError>{errorMessage}</FieldError>
    </Field>
  );
}

interface NumberFieldProps
  extends Omit<React.ComponentProps<typeof RACNumberField>, "value"> {
  value?: number | null;
  label?: React.ReactNode;
  onChange?: (value: number | null) => void;
  errorMessage?: string;
  size?: "large" | "medium" | "small";
  variant?: "default" | "table" | "filter";
  ariaLabel?: string;
}

export function NumberField({
  label,
  errorMessage,
  isDisabled,
  size,
  variant,
  className,
  ...props
}: NumberFieldProps) {
  return (
    <Field label={label}>
      <RACNumberField
        {...props}
        aria-label={props.ariaLabel}
        value={props.value ?? undefined}
        onChange={(value) => {
          props.onChange?.(value ? Number(value) : null);
        }}
        isDisabled={isDisabled}
      >
        <Input
          className={cn(
            inputStyles({
              size,
              variant,
              isError: Boolean(errorMessage),
              isDisabled,
            }),
            className
          )}
        />
      </RACNumberField>
    </Field>
  );
}
